import { Popover, PopoverProps } from "@mail/easyui.popover";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const OverlayDuration = 250;

/**
 * Overlay位置
 * @public
 */
export type OverlayPosition = "center" | "bottom" | "left" | "right" | "top";
/**
 * Overlay内部状态
 * @public
 */
export interface OverlayState {
  appearStatus: AppearStatus;
}

/**
 * Overlay Props
 * @public
 */
export interface OverlayProps {
  position: OverlayPosition;
  children?: React.ReactNode;
  active: boolean;
  backdrop?: boolean | any | ((state: OverlayState) => any);
  onActiveChange?: (active: boolean) => void;
  onAppear?: () => void;
  onDisappear?: () => void;
  popoverProps?: Omit<
    PopoverProps,
    "active" | "onActiveChange" | "contentProps" | "backdropProps" | "children"
  >;
}

/**
 * Overlay组件
 * @public
 */
export const Overlay: React.FC<OverlayProps> = (props) => {
  const {
    position,
    active,
    children,
    onActiveChange,
    onAppear,
    onDisappear,
    backdrop = true,
    popoverProps,
  } = props;
  const [activeDelay, setActiveDelay] = useState(active);
  const appearStatus = getAppearStatus(active, activeDelay);
  const delegateRef = useRef({ appearStatus, onAppear, onDisappear });

  delegateRef.current.onAppear = onAppear;
  delegateRef.current.onDisappear = onDisappear;

  useEffect(() => {
    if (delegateRef.current.appearStatus !== appearStatus) {
      if (appearStatus === AppearStatus.didAppear) {
        if (delegateRef.current.onAppear) {
          delegateRef.current.onAppear();
        }
      } else if (appearStatus === AppearStatus.didDisappear) {
        if (delegateRef.current.onDisappear) {
          delegateRef.current.onDisappear();
        }
      }
    }

    delegateRef.current.appearStatus = appearStatus;
  }, [appearStatus]);

  useEffect(() => {
    if (active) {
      const timer = setTimeout(() => {
        setActiveDelay(true);
      }, 0);

      return () => {
        clearTimeout(timer);
      };
    } else {
      const timer = setTimeout(() => {
        setActiveDelay(false);
      }, OverlayDuration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [active]);

  let backdropProps: any = undefined;

  if (backdrop) {
    if (backdrop === true) {
      backdropProps = {
        as: OverlayBackdrop,
        active: appearStatus === AppearStatus.didAppear,
      };
    } else if (typeof backdrop === "function") {
      backdropProps = backdrop({ appearStatus });
    } else {
      backdropProps = backdrop;
    }
  }

  return (
    <Popover
      active={appearStatus !== AppearStatus.didDisappear}
      onActiveChange={onActiveChange}
      contentProps={{
        as: OverlayContent,
        position,
        active: appearStatus === AppearStatus.didAppear,
      }}
      backdropProps={backdropProps}
      {...popoverProps}
    >
      {children}
    </Popover>
  );
};

export default Overlay;

const OverlayContent = styled("div").withConfig({
  shouldForwardProp: (prop) => ["position", "active"].indexOf(prop) === -1,
})<{
  position: OverlayPosition;
  active?: boolean;
}>`
  position: fixed;
  max-width: 100%;
  max-height: 100%;

  ${({ position, active }) => {
    if (position === "bottom") {
      return `
        top: auto;
        right: 0;
        bottom: 0;
        left: 0;
        transform: ${active ? "none" : "translate3d(0, 100%, 0)"};
        transition: transform ${OverlayDuration}ms;
        display: flex;
        flex-flow: column nowrap;
      `;
    } else if (position === "left") {
      return `
        top: 0;
        right: auto;
        bottom: 0;
        left: 0;
        transform: ${active ? "none" : "translate3d(-100%, 0, 0)"};
        transition: transform ${OverlayDuration}ms;
        display: flex;
        flex-flow: row nowrap;
      `;
    } else if (position === "right") {
      return `
        top: 0;
        right: 0;
        bottom: 0;
        left: auto;
        transform: ${active ? "none" : "translate3d(100%, 0, 0)"};
        transition: transform ${OverlayDuration}ms;
        display: flex;
        flex-flow: row nowrap;
      `;
    } else if (position === "top") {
      return `
        top: 0;
        right: 0;
        bottom: auto;
        left: 0;
        transform: ${active ? "none" : "translate3d(0, -100%, 0)"};
        transition: transform ${OverlayDuration}ms;
        display: flex;
        flex-flow: column nowrap;
      `;
    }

    return `
      top: 50%;
      left: 50%;
      transform: translate3d(-50%, -50%, 0);
      opacity: ${active ? 1 : 0};
      transition: opacity ${OverlayDuration}ms;
    `;
  }}
`;

const OverlayBackdrop = styled("div").withConfig({
  shouldForwardProp: (prop) => ["active"].indexOf(prop) === -1,
})<{ active?: boolean }>`
  position: fixed;
  background: rgba(0, 0, 0, 0.4);
  transition: opacity ${OverlayDuration}ms;

  opacity: ${({ active }) => (active ? 1 : 0)};
`;

enum AppearStatus {
  willAppear,
  didAppear,
  willDisappear,
  didDisappear,
}

/**
 * 获取显示状态
 * @param active
 * @param activeDelay
 * @returns 0: 即将显示; 1: 已显示; 2: 即将隐藏; 3: 已隐藏;
 */
function getAppearStatus(active: boolean, activeDelay: boolean): AppearStatus {
  if (active) {
    return activeDelay ? 1 : 0;
  }
  return activeDelay ? 2 : 3;
}
